'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import { nanoid } from 'nanoid';
import { useAuth } from '@/lib/auth';

export default function NewSplitCausePage() {
  const router = useRouter();
  const { isAuthenticated } = useAuth();
  const [title, setTitle] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;

    setIsCreating(true);

    try {
      // Create a temporary graph ID (no authentication required)
      // Users need to log in only to save their work
      const graphId = nanoid();
      router.push(`/splitcause/editor?id=${graphId}`);
    } catch (error) {
      console.error('Failed to create graph:', error);
      setIsCreating(false);
    }
  };

  return (
    <div className="min-h-screen bg-white dark:bg-background">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2 text-foreground">
            Create New Investigation
          </h1>
          <p className="text-foreground-secondary">
            Start a new root cause analysis investigation
          </p>
        </div>

        <form onSubmit={handleCreate} className="space-y-6">
          <div>
            <label
              htmlFor="title"
              className="block text-sm font-medium text-foreground mb-2"
            >
              Investigation Title
            </label>
            <input
              type="text"
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g., API Latency Spike - Nov 2024"
              className="w-full px-4 py-3 bg-surface border border-border rounded-lg text-foreground placeholder:text-foreground-muted focus:outline-none focus:ring-2 focus:ring-ring"
              required
            />
          </div>

          <div className="bg-surface p-4 rounded-lg border border-border">
            <h3 className="font-medium text-foreground mb-2">What happens next?</h3>
            <ul className="space-y-2 text-sm text-foreground-secondary">
              <li className="flex items-start gap-2">
                <span className="text-accent">→</span>
                <span>You&rsquo;ll be taken to the graph editor</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-accent">→</span>
                <span>Add nodes for system components and hypotheses</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-accent">→</span>
                <span>Connect them with edges to model dependencies</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-accent">→</span>
                <span>Attach evidence and run inference to find root causes</span>
              </li>
              {!isAuthenticated && (
                <li className="flex items-start gap-2 text-accent">
                  <span className="text-accent">ℹ️</span>
                  <span>Log in later to save your investigation</span>
                </li>
              )}
            </ul>
          </div>

          <div className="flex gap-4">
            <button
              type="submit"
              disabled={isCreating || !title.trim()}
              className="flex-1 px-6 py-3 bg-accent text-white font-semibold rounded-lg hover:bg-accent-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isCreating ? 'Creating...' : 'Create Investigation'}
            </button>
            <button
              type="button"
              onClick={() => router.back()}
              className="px-6 py-3 border-2 border-border text-foreground font-semibold rounded-lg hover:bg-surface transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>

        <div className="mt-12 border-t border-border pt-8">
          <h3 className="font-semibold text-foreground mb-4">Quick Tips</h3>
          <div className="space-y-3 text-sm text-foreground-secondary">
            <div>
              <strong className="text-foreground">Components</strong> - Represent system parts (API, Database, Cache, etc.)
            </div>
            <div>
              <strong className="text-foreground">Hypotheses</strong> - Potential root causes with probability estimates
            </div>
            <div>
              <strong className="text-foreground">Evidence</strong> - Observations, test results, logs, or metrics
            </div>
            <div>
              <strong className="text-foreground">Conflicts</strong> - Mark hypotheses as mutually exclusive for better inference
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
